import { useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Loader2, Send, Building2, CheckCircle2 } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const FEATURES_LIST = [
  { id: 'unlimited_locations', label: 'Unlimited Locations' },
  { id: 'inventory', label: 'Inventory Management' },
  { id: 'route_planning', label: 'Route Planning & Maps' },
  { id: 'location_portal', label: 'Location Portal' },
  { id: 'partner_management', label: 'Partner Management' },
  { id: 'activity_logs', label: 'Activity Logs & Audit Trail' },
  { id: 'multi_company', label: 'Multi-Company Support' },
  { id: 'custom_integrations', label: 'Custom Integrations' },
  { id: 'api_access', label: 'API Access' },
  { id: 'sso', label: 'Single Sign-On (SSO)' },
];

const TIMELINE_OPTIONS = [
  { value: 'immediate', label: 'Immediately' },
  { value: '1-3_months', label: '1-3 Months' },
  { value: '3-6_months', label: '3-6 Months' },
  { value: '6+_months', label: '6+ Months' },
  { value: 'just_exploring', label: 'Just Exploring' },
];

const ContactSalesModal = ({ open, onOpenChange, source = 'landing_page' }) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    job_title: '',
    estimated_locations: '',
    current_solution: '',
    timeline: '',
    message: '',
    features_interested: [],
  });

  const handleFeatureToggle = (featureId) => {
    setFormData((prev) => ({
      ...prev,
      features_interested: prev.features_interested.includes(featureId)
        ? prev.features_interested.filter((f) => f !== featureId)
        : [...prev.features_interested, featureId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.company_name || !formData.contact_name || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_BASE}/api/enterprise-inquiries/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          estimated_locations: formData.estimated_locations ? parseInt(formData.estimated_locations, 10) : null,
          source: source,
        }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setSubmitted(true);
        toast.success(data.message);
      } else {
        toast.error(data.detail || 'Failed to submit inquiry');
      }
    } catch (error) {
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset form after modal closes
    setTimeout(() => {
      setSubmitted(false);
      setFormData({
        company_name: '',
        contact_name: '',
        email: '',
        phone: '',
        job_title: '',
        estimated_locations: '',
        current_solution: '',
        timeline: '',
        message: '',
        features_interested: [],
      });
    }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-neutral-900 border-amber-500/20 text-white">
        {submitted ? (
          <div className="py-12 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold mb-4">Thank You!</h2>
            <p className="text-gray-400 mb-6 max-w-md mx-auto">
              Our enterprise team has received your inquiry and will contact you within 24-48 hours.
            </p>
            <Button
              onClick={handleClose}
              className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-bold"
              data-testid="contact-sales-close-btn"
            >
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3 text-2xl">
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-amber-400" />
                </div>
                Enterprise Inquiry
              </DialogTitle>
              <DialogDescription className="text-gray-400">
                Tell us about your business needs and our team will create a custom solution for you.
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              {/* Contact Information */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-wider">
                  Contact Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="company_name" className="text-gray-300">
                      Company Name <span className="text-red-400">*</span>
                    </Label>
                    <Input
                      id="company_name"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      className="bg-neutral-800 border-neutral-700 text-white focus:border-amber-500"
                      placeholder="Your company name"
                      data-testid="contact-sales-company"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_name" className="text-gray-300">
                      Your Name <span className="text-red-400">*</span>
                    </Label>
                    <Input
                      id="contact_name"
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      className="bg-neutral-800 border-neutral-700 text-white focus:border-amber-500"
                      placeholder="Your full name"
                      data-testid="contact-sales-name"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-gray-300">
                      Email <span className="text-red-400">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="bg-neutral-800 border-neutral-700 text-white focus:border-amber-500"
                      placeholder="you@company.com"
                      data-testid="contact-sales-email"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-gray-300">Phone</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="bg-neutral-800 border-neutral-700 text-white focus:border-amber-500"
                      placeholder="+1 (555) 000-0000"
                      data-testid="contact-sales-phone"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="job_title" className="text-gray-300">Job Title</Label>
                    <Input
                      id="job_title"
                      value={formData.job_title}
                      onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                      className="bg-neutral-800 border-neutral-700 text-white focus:border-amber-500"
                      placeholder="e.g., Operations Manager"
                      data-testid="contact-sales-title"
                    />
                  </div>
                </div>
              </div>
              
              {/* Business Details */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-wider">
                  Business Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="estimated_locations" className="text-gray-300">
                      Estimated # of Locations
                    </Label>
                    <Input
                      id="estimated_locations"
                      type="number"
                      min="1"
                      value={formData.estimated_locations}
                      onChange={(e) => setFormData({ ...formData, estimated_locations: e.target.value })}
                      className="bg-neutral-800 border-neutral-700 text-white focus:border-amber-500"
                      placeholder="e.g., 50"
                      data-testid="contact-sales-locations"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="timeline" className="text-gray-300">Timeline</Label>
                    <Select
                      value={formData.timeline}
                      onValueChange={(value) => setFormData({ ...formData, timeline: value })}
                    >
                      <SelectTrigger 
                        className="bg-neutral-800 border-neutral-700 text-white focus:border-amber-500"
                        data-testid="contact-sales-timeline"
                      >
                        <SelectValue placeholder="When are you looking to start?" />
                      </SelectTrigger>
                      <SelectContent className="bg-neutral-800 border-neutral-700">
                        {TIMELINE_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value} className="text-white hover:bg-neutral-700">
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="current_solution" className="text-gray-300">
                      Current Solution
                    </Label>
                    <Input
                      id="current_solution"
                      value={formData.current_solution}
                      onChange={(e) => setFormData({ ...formData, current_solution: e.target.value })}
                      className="bg-neutral-800 border-neutral-700 text-white focus:border-amber-500"
                      placeholder="What are you currently using? (e.g., spreadsheets, another software)"
                      data-testid="contact-sales-current"
                    />
                  </div>
                </div>
              </div>
              
              {/* Features Interested */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-wider">
                  Features of Interest
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {FEATURES_LIST.map((feature) => (
                    <div key={feature.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={feature.id}
                        checked={formData.features_interested.includes(feature.id)}
                        onCheckedChange={() => handleFeatureToggle(feature.id)}
                        className="border-neutral-600 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                        data-testid={`contact-sales-feature-${feature.id}`}
                      />
                      <Label 
                        htmlFor={feature.id} 
                        className="text-sm text-gray-300 cursor-pointer"
                      >
                        {feature.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="message" className="text-gray-300">
                  Additional Information
                </Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white focus:border-amber-500 min-h-[100px]"
                  placeholder="Tell us more about your specific needs, challenges, or questions..."
                  data-testid="contact-sales-message"
                />
              </div>
              
              {/* Submit Button */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  className="flex-1 border-neutral-700 text-gray-300 hover:bg-neutral-800"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-bold"
                  data-testid="contact-sales-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Inquiry
                    </>
                  )}
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ContactSalesModal;
